import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

interface ColumnPreferences {
  order: string[];
  visibility: Record<string, boolean>;
  tableName: string;
  timestamp: number;
}

export interface TrendsPreferences {
  analysisLevel: 'listings' | 'skus';
  viewMode: 'matrix' | 'timeline';
  expandSkus: boolean;
  timestamp: number;
}

interface UserSettings {
  columnPreferences?: {
    [tableName: string]: ColumnPreferences;
  };
  trendsPreferences?: TrendsPreferences;
}

/**
 * Guarda las preferencias de columnas para un usuario en Firestore
 * @param userId - UID del usuario
 * @param tableName - Nombre de la tabla
 * @param order - Orden de las columnas
 * @param visibility - Visibilidad de las columnas
 */
export const saveColumnPreferences = async (
  userId: string,
  tableName: string,
  order: string[],
  visibility: Record<string, boolean>
): Promise<void> => {
  try {
    const userSettingsRef = doc(db, 'users', userId, 'settings', 'preferences');

    const preferences: ColumnPreferences = {
      order,
      visibility,
      tableName,
      timestamp: Date.now()
    };

    const settingsSnap = await getDoc(userSettingsRef);

    if (settingsSnap.exists()) {
      await updateDoc(userSettingsRef, {
        [`columnPreferences.${tableName}`]: preferences
      });
    } else {
      await setDoc(userSettingsRef, {
        columnPreferences: {
          [tableName]: preferences
        }
      });
    }
  } catch (error) {
    console.error('Error guardando preferencias de columnas:', error);
  }
};

/**
 * Carga las preferencias de columnas para un usuario desde Firestore
 * @param userId - UID del usuario
 * @param tableName - Nombre de la tabla
 * @returns Preferencias de columnas o null si no existen
 */
export const loadColumnPreferences = async (
  userId: string,
  tableName: string
): Promise<ColumnPreferences | null> => {
  try {
    const userSettingsRef = doc(db, 'users', userId, 'settings', 'preferences');

    const settingsSnap = await getDoc(userSettingsRef);

    if (settingsSnap.exists()) {
      const settings = settingsSnap.data() as UserSettings;
      const preferences = settings.columnPreferences?.[tableName];

      if (preferences && preferences.tableName === tableName) {
        return preferences;
      }
    }

    return null;
  } catch (error) {
    console.error('Error cargando preferencias de columnas:', error);
    return null;
  }
};

/**
 * Migra preferencias de localStorage a Firestore
 * Esta función se ejecutará una sola vez para migrar datos existentes
 * @param userId - UID del usuario
 */
export const migrateLocalStorageToFirestore = async (userId: string): Promise<void> => {
  try {
    const migrationKey = `columnPreferences_migrated_${userId}`;

    // Verificar si ya se migró
    if (localStorage.getItem(migrationKey)) {
      return;
    }

    // Buscar todas las claves de preferencias en localStorage
    const keysToMigrate: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('columnPreferences_') && !key.includes('migrated')) {
        keysToMigrate.push(key);
      }
    }

    if (keysToMigrate.length === 0) {
      localStorage.setItem(migrationKey, 'true');
      return;
    }

    // Migrar cada tabla
    for (const key of keysToMigrate) {
      const tableName = key.replace('columnPreferences_', '');
      const data = localStorage.getItem(key);

      if (data) {
        try {
          const preferences = JSON.parse(data);
          await saveColumnPreferences(
            userId,
            tableName,
            preferences.order || [],
            preferences.visibility || {}
          );
        } catch (parseError) {
          console.error(`Error migrando tabla ${tableName}:`, parseError);
        }
      }
    }

    // Marcar como migrado
    localStorage.setItem(migrationKey, 'true');

  } catch (error) {
    console.error('Error en migración de preferencias:', error);
  }
};

/**
 * Guarda las preferencias de tendencias para un usuario en Firestore
 * @param userId - UID del usuario
 * @param preferences - Preferencias de tendencias
 */
export const saveTrendsPreferences = async (
  userId: string,
  preferences: Omit<TrendsPreferences, 'timestamp'>
): Promise<void> => {
  try {
    const userSettingsRef = doc(db, 'users', userId, 'settings', 'preferences');

    const trendsPreferences: TrendsPreferences = {
      ...preferences,
      timestamp: Date.now()
    };

    const settingsSnap = await getDoc(userSettingsRef);

    if (settingsSnap.exists()) {
      await updateDoc(userSettingsRef, {
        trendsPreferences
      });
    } else {
      await setDoc(userSettingsRef, {
        trendsPreferences
      });
    }
  } catch (error) {
    console.error('Error guardando preferencias de tendencias:', error);
  }
};

/**
 * Carga las preferencias de tendencias para un usuario desde Firestore
 * @param userId - UID del usuario
 * @returns Preferencias de tendencias o null si no existen
 */
export const loadTrendsPreferences = async (
  userId: string
): Promise<TrendsPreferences | null> => {
  try {
    const userSettingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    const settingsSnap = await getDoc(userSettingsRef);

    if (settingsSnap.exists()) {
      const settings = settingsSnap.data() as UserSettings;
      if (settings.trendsPreferences) {
        return settings.trendsPreferences;
      }
    }

    return null;
  } catch (error) {
    console.error('Error cargando preferencias de tendencias:', error);
    return null;
  }
};

// ----------------------- Admin: orden de cards -----------------------

export interface AdminClientOrder {
  real: string[];   // uids en orden custom (panel "Clientes")
  demo: string[];   // uids en orden custom (panel "Demos")
  timestamp: number;
}

/**
 * Guarda el orden custom del admin para los paneles Clientes/Demos.
 */
export const saveAdminClientOrder = async (
  userId: string,
  order: { real?: string[]; demo?: string[] }
): Promise<void> => {
  try {
    const ref = doc(db, 'users', userId, 'settings', 'preferences');
    const snap = await getDoc(ref);
    const prev = snap.exists() ? (snap.data() as any).adminClientOrder || {} : {};
    const next: AdminClientOrder = {
      real: order.real ?? prev.real ?? [],
      demo: order.demo ?? prev.demo ?? [],
      timestamp: Date.now(),
    };
    if (snap.exists()) {
      await updateDoc(ref, { adminClientOrder: next });
    } else {
      await setDoc(ref, { adminClientOrder: next });
    }
  } catch (e) {
    console.error('Error saving admin client order:', e);
  }
};

export const loadAdminClientOrder = async (
  userId: string
): Promise<AdminClientOrder | null> => {
  try {
    const ref = doc(db, 'users', userId, 'settings', 'preferences');
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const o = (snap.data() as any).adminClientOrder;
    if (!o) return null;
    return { real: o.real || [], demo: o.demo || [], timestamp: o.timestamp || 0 };
  } catch (e) {
    console.error('Error loading admin client order:', e);
    return null;
  }
};

// ----------------- Listados reusables (DataHealthList) -----------------
// Preferencias por-listado (resize + reorder de columnas) persistidas por
// usuario en el MISMO doc `users/{uid}/settings/preferences`, bajo el map
// `listPreferences.{listId}`. Cada listId identifica un uso concreto de
// DataHealthList (ej. "datahealth-dudosos", "config-usuarios").

export interface ListPreferences {
  /** Orden de columnas por key (DataHealthColumn.key). */
  order: string[];
  /** Ancho en px por key de columna. */
  widths: Record<string, number>;
  /** Visibilidad por key (opcional — reservado para v2). */
  visibility?: Record<string, boolean>;
  timestamp: number;
}

/**
 * Guarda las preferencias (orden + anchos) de un listado reusable para el usuario.
 * @param userId - UID del usuario
 * @param listId - id estable del listado (ej. "datahealth-dudosos")
 * @param prefs - { order, widths, visibility? }
 */
export const saveListPreferences = async (
  userId: string,
  listId: string,
  prefs: { order?: string[]; widths?: Record<string, number>; visibility?: Record<string, boolean> }
): Promise<void> => {
  try {
    const ref = doc(db, 'users', userId, 'settings', 'preferences');
    const snap = await getDoc(ref);
    const prev: Partial<ListPreferences> = snap.exists()
      ? ((snap.data() as any).listPreferences?.[listId] || {})
      : {};
    const next: ListPreferences = {
      order: prefs.order ?? prev.order ?? [],
      widths: prefs.widths ?? prev.widths ?? {},
      visibility: prefs.visibility ?? prev.visibility,
      timestamp: Date.now(),
    };
    if (next.visibility === undefined) delete next.visibility;
    if (snap.exists()) {
      await updateDoc(ref, { [`listPreferences.${listId}`]: next });
    } else {
      await setDoc(ref, { listPreferences: { [listId]: next } });
    }
  } catch (e) {
    console.error('Error guardando preferencias de listado:', e);
  }
};

/**
 * Carga las preferencias (orden + anchos) de un listado reusable para el usuario.
 * @param userId - UID del usuario
 * @param listId - id estable del listado
 * @returns ListPreferences o null si no existen
 */
export const loadListPreferences = async (
  userId: string,
  listId: string
): Promise<ListPreferences | null> => {
  try {
    const ref = doc(db, 'users', userId, 'settings', 'preferences');
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const p = (snap.data() as any).listPreferences?.[listId];
    if (!p) return null;
    return {
      order: p.order || [],
      widths: p.widths || {},
      visibility: p.visibility,
      timestamp: p.timestamp || 0,
    };
  } catch (e) {
    console.error('Error cargando preferencias de listado:', e);
    return null;
  }
};
